import {
  Button,
  Flex,
  Portal,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Textarea,
  useDisclosure,
} from '@chakra-ui/react';
import { DATATABLE_COLLAPSE, DATATABLE_EXPAND } from 'constants/mixpanel';
import { Mixpanel } from 'libs/third-party/Mixpanel/mixpanel';
import { ChangeEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GetWidgetMemberComponent } from 'widgets/Widget.types';
import { useEditor, useWidget } from 'widgets/sdk';

import { convertToWordCount } from '../ChartWidget.helpers';
import { ChartWidgetData } from '../ChartWidget.types';
import { ChartDataTable } from '../DataTableChartCommon/DataTableChartCommonMenuSettings';
import { DataTableExpandModal } from '../DataTableChartCommon/DataTableExpandModal';
import { ImportData } from '../DataTableChartCommon/ImportData';

const PassageSubTab = () => {
  const {
    generalOptions: { passage = '' },
    updateWidget,
  } = useWidget<ChartWidgetData>();
  const { t } = useTranslation('chart_widget', { useSuspense: false, keyPrefix: 'wordcloud' });
  const placeHolder = t('passagePlaceHolder');

  const [cache, setCache] = useState<string>(passage);

  const onChangePassage = (event: ChangeEvent<HTMLTextAreaElement>) => {
    setCache(event.target.value);
  };

  const onBlurPassage = () => {
    if (passage !== cache) {
      updateWidget({
        generalOptions: { passage: cache },
        seriesData: convertToWordCount(cache),
      });
    }
  };

  return (
    <Textarea
      height={'2xs'}
      placeholder={placeHolder}
      value={cache}
      onChange={onChangePassage}
      onBlur={onBlurPassage}
      _placeholder={{ color: 'font.300' }}
    />
  );
};

interface WordCloudDataTabProps {
  getWidget?: GetWidgetMemberComponent;
}

export const WordCloudDataTab = ({ getWidget }: WordCloudDataTabProps) => {
  const { t } = useTranslation('chart_widget', { useSuspense: false, keyPrefix: 'wordcloud' });
  const { t: tButton } = useTranslation('editor_side_menu_settings', { useSuspense: false });
  const wordListLabel = t('wordList');
  const passageLabel = t('passage');
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { disableEditorKeyboardShortcuts, enableEditorKeyboardShortcuts } = useEditor();

  const [tabIdx, setTabIdx] = useState<number>(0);

  const handleOpenDataTableModal = () => {
    disableEditorKeyboardShortcuts();
    Mixpanel.track(DATATABLE_EXPAND.eventName);
    onOpen();
  };

  const handleCloseDataTableModal = () => {
    enableEditorKeyboardShortcuts();
    Mixpanel.track(DATATABLE_COLLAPSE.eventName);
    onClose();
  };

  return (
    <Tabs index={tabIdx} onChange={setTabIdx} variant={'sub-menu'}>
      <TabList>
        <Tab>{wordListLabel}</Tab>
        <Tab>{passageLabel}</Tab>
      </TabList>
      <TabPanels>
        <TabPanel height='full'>
          {tabIdx === 0 && (
            <Flex direction='column' height='full' gap={4}>
              <ImportData />
              {getWidget && (
                <>
                  <Portal>
                    <DataTableExpandModal
                      isOpen={isOpen}
                      onClose={handleCloseDataTableModal}
                      getWidget={getWidget}
                      component={<ChartDataTable colHeaders />}
                    />
                  </Portal>
                  <Flex justifyContent='flex-end'>
                    <Button onClick={handleOpenDataTableModal} variant={'outline'} size={'sm'}>
                      {tButton('chartSettings.expandButton')}
                    </Button>
                  </Flex>
                </>
              )}
              <ChartDataTable colHeaders />
            </Flex>
          )}
        </TabPanel>
        <TabPanel>
          <PassageSubTab />
        </TabPanel>
      </TabPanels>
    </Tabs>
  );
};
